import { cn } from '~/common/utils/styleUtil';
import { AddToCart } from '~/features/catalog/components/AddToCart';
import { ArticleSellUnit } from '~/features/catalog/components/article/ArticleSellUnit';
import { ArticlePrice } from '~/features/catalog/components/article/ArticlePrice';
import { ArticleSecondaryText } from '~/features/catalog/components/article/ArticleSecondaryText';
import { useArticleDetailPath } from '~/features/catalog/hooks/useArticleDetailPath';
import type { BghArticle } from '~/common/types/bghArticle';
import { useCatalogBghArticle } from '~/features/catalog/hooks/useCatalogBghArticle';
import { TgsArticleImage } from '~/common/components/ArticleImage';
import { Link } from 'react-router';
import { CatalogArticleNotAvailable } from '~/features/catalog/components/CatalogArticleNotAvailable';
import { SalesOrderSetInfo } from '~/features/catalog/components/article/SalesOrderSetInfo';
import { ArticleInfoButton } from '~/features/catalog/components/ArticleInfoButton';
import { PriceBadgeGroup } from '~/common/components/PriceBadgeGroup.tsx';
import { useGkAirTracking } from '~/features/catalog/routes/resources/GkAirTrackingRoute.tsx';

type CatalogListItemProps = {
  article: BghArticle;
  isAuthenticated: boolean;
  className?: string;
  isSingleItemInRow?: boolean;
};

export function CatalogBghTileItem({
  article,
  isAuthenticated,
  className,
  isSingleItemInRow = false
}: Readonly<CatalogListItemProps>) {
  const catalogArticle = useCatalogBghArticle(article);
  const articleDetailPath = useArticleDetailPath(catalogArticle.articleNumber);
  const trackGkAirClick = useGkAirTracking(catalogArticle.articleNumber, catalogArticle.gkAirTrackingToken);

  return (
    <article
      className={cn(
        'group hover:bg-neutral-50 h-full flex flex-col justify-between',
        className,
        isSingleItemInRow ? 'border-b pb-8' : 'p-4'
      )}
    >
      <Link
        id={catalogArticle.articleNumber}
        to={articleDetailPath}
        className={'flex flex-col justify-between flex-1'}
        data-testid={`article-link-${catalogArticle.articleNumber}`}
        state={{ quantity: catalogArticle.quantity }}
        onClick={() => (catalogArticle.gkAirTrackingToken ? trackGkAirClick() : undefined)}
      >
        <div className={'pb-8'}>
          <div className={'relative flex justify-center p-2 w-full'}>
            <TgsArticleImage
              celumId={catalogArticle.celumId}
              articleNumber={catalogArticle.articleNumber}
              alt={catalogArticle.description}
            />
            <div className={cn('absolute right-6 flex flex-col gap-2', !isSingleItemInRow && 'right-0')}>
              {catalogArticle.isInSalesOrderSet && <SalesOrderSetInfo />}
              <ArticleInfoButton article={article} reserveSpace={false} />
            </div>
            {catalogArticle.isInSalesOrderSet && (
              <SalesOrderSetInfo className={cn('absolute right-6', !isSingleItemInRow && 'right-0')} />
            )}
          </div>
          <ArticlePrice
            price={catalogArticle.price}
            oldPrice={catalogArticle.oldPrice}
            isAction={catalogArticle.isAction}
            actionValidTo={catalogArticle.actionValidTo}
            unitText={catalogArticle.unitText}
            isHit={catalogArticle.isHit}
          />
          <div
            className={cn(
              'pt-2 text-base font-bold line-clamp-2',
              catalogArticle.isAction && !catalogArticle.isHit && 'text-primary'
            )}
            data-testid={`article-description-${catalogArticle.articleNumber}`}
          >
            {catalogArticle.description}
          </div>
          <ArticleSecondaryText
            articleNumber={catalogArticle.articleNumber}
            rohstoffHerkunft={catalogArticle.rohstoffHerkunft}
            brand={catalogArticle.brand}
            lineClamp={3}
            tile={true}
          />
          <PriceBadgeGroup
            direction="wrap"
            badges={[
              ...(catalogArticle.graduatedPriceSteps?.length ? [{ type: 'gross' as const }] : []),
              ...(catalogArticle.noveltyUntil ? [{ type: 'new' as const }] : []),
              ...(isAuthenticated && catalogArticle.isHit ? [{ type: 'hit' as const }] : [])
            ]}
          />
        </div>
      </Link>
      <div>
        <ArticleSellUnit
          selectedArticle={catalogArticle}
          allArticles={catalogArticle.allArticles}
          onArticleChange={changeInfo => catalogArticle.setSelectedArticleNumber(changeInfo.articleNumber.toString())}
        />
      </div>

      <div>
        <div className={'flex'}>
          <AddToCart
            articleNumber={catalogArticle.articleNumber}
            articleDescriptionText={catalogArticle.description}
            mainArticleId={catalogArticle.mainArticleId}
            isExternalArticle={false}
            isProfitPlus={catalogArticle.isProfitPlus}
            calculatedTotalPriceText={catalogArticle.calculatedTotalPriceText}
            quantity={catalogArticle.quantity}
            onQuantityChange={catalogArticle.setQuantity}
            isAuthenticated={isAuthenticated}
            writeArticleQuantityCache={true}
            loadArticleQuantityCache={true}
            hideCart={catalogArticle.hideCart}
            hideAddToTemplate={catalogArticle.isInactive}
          />
        </div>

        {(catalogArticle.hideCart || catalogArticle.showSellOffMessage) && (
          <CatalogArticleNotAvailable
            showSellOffMessage={catalogArticle.showSellOffMessage}
            showCurrentlyNotAvailableMessage={catalogArticle.showCurrentlyNotAvailableMessage}
            substituteArticleNumber={catalogArticle.substituteArticleNumber}
            substituteArticleText={catalogArticle.substituteArticleText}
            notInWebshop={catalogArticle.notInWebshop}
            className={'mt-2'}
          />
        )}
      </div>
    </article>
  );
}
